/**
 * „Melden“ (0176): Nutzer melden einen Fehler direkt aus der Meldung heraus. Mitgeschickt werden die letzten 30 Schritte
 * (Seitenwechsel, geklickte Knöpfe mit ihrer Beschriftung, Meldungen), technische Spuren (JS-Fehler mit Stack, unbehandelte
 * Promises, console.error, fehlgeschlagene Server-Anfragen ohne Parameter) und auf Wunsch ein Bild der Seite.
 * Nie mitgeschickt: Eingabewerte, Inhalte von Anfragen, Tokens. Im Bild sind Eingabefelder (auch Passwort- und Kartenfelder)
 * und mit data-sensibel markierte Bereiche geschwärzt; die Datenbank entfernt zusätzlich E-Mails, IBANs, Tokens und lange Zahlen
 * (wie 0158). 0181: Schon im Browser entfernt maskSecrets Schlüssel, Tokens (auch #access_token= im Anker), Stripe-, Meta- und
 * Instagram-Schlüssel, IBAN und Kartennummern aus Schritten, Spuren und Notiz.
 */
import { maskSecrets } from "@talentkontor/domain";
import { browserKurz, fehlerAgentur, portalVon } from "./fehlerbericht";

export type Schritt = { t: "seite" | "klick" | "meldung"; at: string; text: string };
export type Spur = { t: "fehler" | "promise" | "konsole" | "anfrage"; at: string; text: string; stack?: string; status?: number };

const MAX_SCHRITTE = 30;
const MAX_SPUREN = 20;
const schritte: Schritt[] = [];
const spuren: Spur[] = [];
let rolle: string | undefined;
let aktiv = false;

const jetzt = () => new Date().toISOString();
const kurz = (s: unknown, n = 300) => maskSecrets(String(s ?? "").replace(/\s+/g, " ").trim()).slice(0, n);

/** Pfad ohne Abfrage und Anker; IDs und lange Kennungen werden zu Platzhaltern (wie app.client_error_path). */
export function pfadOhneDaten(p: string): string {
  return p.split("?")[0]!.split("#")[0]!
    .replace(/\/[0-9a-fA-F-]{32,36}(?=\/|$)/g, "/:id")
    .replace(/\/[A-Za-z0-9_-]{16,}(?=\/|$)/g, "/:token")
    .slice(0, 200);
}
/** Adresse einer Anfrage: Host und Pfad ohne Parameter, IDs als Platzhalter */
export function adresseOhneDaten(u: string): string {
  try {
    const x = new URL(u, location.origin);
    return (x.host === location.host ? "" : x.host) + pfadOhneDaten(x.pathname);
  } catch {
    return "?";
  }
}

function merke<T>(liste: T[], max: number, eintrag: T) {
  liste.push(eintrag);
  if (liste.length > max) liste.splice(0, liste.length - max);
}
export function merkeSchritt(t: Schritt["t"], text: string) {
  if (!text) return;
  merke(schritte, MAX_SCHRITTE, { t, at: jetzt(), text: kurz(text, 160) });
}
export function merkeSpur(s: Omit<Spur, "at">) {
  merke(spuren, MAX_SPUREN, { ...s, at: jetzt(), text: kurz(s.text, 500), stack: s.stack ? kurz(s.stack, 1500) : undefined });
}
export function setMeldenRolle(r: string | undefined) {
  rolle = r;
}

/** Beschriftung eines geklickten Bedienelements (nie Eingabewerte) */
export function beschriftung(el: Element | null): string {
  const ziel = el?.closest("button, a, [role=button], [role=tab], [role=menuitem], summary, label");
  if (!ziel) return "";
  const aria = ziel.getAttribute("aria-label");
  const text = aria || (ziel.tagName === "LABEL" ? "" : ziel.textContent) || "";
  const art = ziel.tagName === "A" ? "Link" : "Knopf";
  return text.trim() ? `${art}: ${kurz(text, 80)}` : "";
}

/** Einmal beim Start: Schritte und technische Spuren mitschreiben (nur im Speicher dieser Seite). */
export function setupMelden() {
  if (aktiv || typeof window === "undefined") return;
  aktiv = true;
  merkeSchritt("seite", pfadOhneDaten(location.pathname));
  const seite = () => merkeSchritt("seite", pfadOhneDaten(location.pathname));
  for (const m of ["pushState", "replaceState"] as const) {
    const orig = history[m].bind(history);
    history[m] = ((...a: Parameters<History["pushState"]>) => {
      const vorher = location.pathname;
      orig(...a);
      if (location.pathname !== vorher) seite();
    }) as History["pushState"];
  }
  window.addEventListener("popstate", seite);
  document.addEventListener("click", (e) => merkeSchritt("klick", beschriftung(e.target as Element)), { capture: true });
  window.addEventListener("error", (ev) => {
    if (ev instanceof ErrorEvent) merkeSpur({ t: "fehler", text: ev.message, stack: (ev.error as Error | undefined)?.stack });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const r = ev.reason as { message?: string; stack?: string } | undefined;
    merkeSpur({ t: "promise", text: r?.message ?? String(ev.reason), stack: r?.stack });
  });
  const ce = console.error.bind(console);
  console.error = (...a: unknown[]) => {
    try { merkeSpur({ t: "konsole", text: a.map((x) => (x instanceof Error ? x.message : typeof x === "string" ? x : "")).join(" ") }); } catch { /* nie stören */ }
    ce(...a);
  };
  // Fehlgeschlagene Anfragen: Adresse ohne Parameter, Status und Fehlertext der Antwort (nie der Inhalt der Anfrage)
  const f = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    try {
      const res = await f(input, init);
      if (!res.ok && res.status !== 304) {
        let text = "";
        try {
          const j = await res.clone().json() as { message?: string; error?: string; msg?: string; hint?: string; code?: string };
          text = [j.code, j.message ?? j.error ?? j.msg, j.hint].filter(Boolean).join(" · ");
        } catch { /* kein JSON */ }
        merkeSpur({ t: "anfrage", status: res.status, text: `${(init?.method ?? "GET").toUpperCase()} ${adresseOhneDaten(url)}${text ? ` · ${text}` : ""}` });
      }
      return res;
    } catch (e) {
      merkeSpur({ t: "anfrage", status: 0, text: `${(init?.method ?? "GET").toUpperCase()} ${adresseOhneDaten(url)} · ${(e as Error).message}` });
      throw e;
    }
  };
}

/** Bezug der Seite: IDs aus dem Pfad mit dem Abschnitt davor, offenes Fenster, Sprache, Bildschirm */
export function seitenBezug(): Record<string, string> {
  const teile = location.pathname.split("/").filter(Boolean);
  const bezug: Record<string, string> = {};
  teile.forEach((teil, i) => {
    if (/^[0-9a-f-]{36}$/i.test(teil) && i > 0) bezug[teile[i - 1]!] = teil;
  });
  const fenster = document.querySelector("dialog[open] h2, dialog[open] h1, [role=dialog] h2")?.textContent;
  if (fenster) bezug.fenster = kurz(fenster, 120);
  const reiter = document.querySelector("[role=tab][aria-selected=true], .steps .current, nav [aria-current=page]")?.textContent;
  if (reiter) bezug.reiter = kurz(reiter, 80);
  bezug.sprache = document.documentElement.lang || "";
  bezug.bildschirm = `${window.innerWidth}×${window.innerHeight}`;
  bezug.darstellung = document.documentElement.dataset.theme ?? "";
  return bezug;
}

/**
 * Bild der sichtbaren Seite als JPEG (höchstens etwa 550 KB). Eingabefelder, Textfelder und mit data-sensibel markierte
 * Bereiche werden geschwärzt. Fremde Bilder ohne Freigabe erscheinen als graue Fläche. Schlägt etwas fehl: null.
 */
export async function bildDerSeite(): Promise<string | null> {
  const root = document.documentElement;
  root.classList.add("co-melden-foto");
  try {
    const { toCanvas } = await import("html-to-image");
    const breite = window.innerWidth, hoehe = window.innerHeight;
    const canvas = await toCanvas(document.body, {
      width: breite, height: hoehe, pixelRatio: Math.min(1, 1280 / breite), skipFonts: true, cacheBust: false,
      imagePlaceholder: "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==",
      style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)` },
      // Melden-Fenster selbst und Knöpfe für die Barrierefreiheit nicht abbilden
      filter: (n) => !(n instanceof HTMLElement && (n.dataset.meldenAus === "1" || n.classList.contains("skip"))),
    });
    for (const q of [0.6, 0.45, 0.3]) {
      const url = canvas.toDataURL("image/jpeg", q);
      if (url.length < 560_000) return url;
    }
    return null;
  } catch {
    return null;
  } finally {
    root.classList.remove("co-melden-foto");
  }
}

export interface Meldung {
  message: string;
  user_note?: string;
  screenshot?: string | null;
  /** Bezug zum Zeitpunkt des Öffnens (sonst wäre das Melden-Fenster selbst „das offene Fenster“) */
  bezug?: Record<string, string>;
}
/** Bericht für public.report_error */
export function meldeBericht(m: Meldung): Record<string, unknown> {
  const portal = portalVon(location.pathname);
  return {
    portal: portal === "oeffentlich" ? "konto" : location.pathname.startsWith("/einrichtung") ? "einrichtung" : portal,
    org_id: fehlerAgentur(),
    role: rolle,
    page: location.pathname,
    message: kurz(m.message, 1000) || "(ohne Text)",
    user_note: m.user_note ? maskSecrets(m.user_note).slice(0, 2000) : undefined,
    context: m.bezug ?? seitenBezug(),
    trail: schritte.slice(-MAX_SCHRITTE),
    tech: spuren.slice(-MAX_SPUREN),
    release: String(import.meta.env.VITE_RELEASE ?? "lokal"),
    browser: browserKurz(navigator.userAgent),
    screenshot: m.screenshot ?? undefined,
  };
}

// ── Melden-Fenster öffnen (von Toasts, Fehlerseiten, Hilfe) ─────────────────────
type Oeffnen = (m: { message: string; screenshot: string | null; bezug: Record<string, string> }) => void;
let oeffner: Oeffnen | null = null;
export function setMeldenOeffner(o: Oeffnen | null) {
  oeffner = o;
}
/** Bild der Seite aufnehmen, bevor das Fenster aufgeht (sonst wäre das Fenster im Bild) */
export async function oeffneMelden(message: string) {
  merkeSchritt("meldung", `Melden: ${message}`);
  const bezug = seitenBezug();
  const screenshot = await bildDerSeite();
  oeffner?.({ message, screenshot, bezug });
}
export function meldenVerfuegbar(): boolean {
  return oeffner !== null;
}
